<template>
  <transition name="prr-filters-modal" appear>
    <div
      v-if="value"
      class="prr-filters-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prr-filters-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="prr-filters-modal__card">
        <div class="prr-filters-modal__header">
          <span id="prr-filters-modal-title" class="prr-filters-modal__title"
            >Filter roles and responsibilities</span
          >
          <button
            type="button"
            class="prr-filters-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="prr-filters-modal__filters-row">
          <div class="prr-filters-modal__column">
            <span class="prr-filters-modal__column-title">Status</span>
            <div class="prr-filters-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="prr-filters-modal__column">
            <span class="prr-filters-modal__column-title">Archived</span>
            <RadioInput
              label="Archived"
              :available-items="archivedOptions"
              :checked-item="archived"
              :unchecked-value="archivedClearKey"
              @changeradio="onArchivedChange"
            />
          </div>

          <div class="prr-filters-modal__column">
            <span class="prr-filters-modal__column-title">Type</span>
            <div class="prr-filters-modal__options">
              <CheckboxInput
                v-for="option in typeOptions"
                :key="option.value"
                :label="option.label"
                :value="types[option.value]"
                @input="updateType(option.value, $event)"
              />
            </div>
          </div>
        </div>

        <div class="prr-filters-modal__divider" />

        <div class="prr-filters-modal__footer">
          <button type="button" class="prr-filters-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  value: { type: Boolean, default: false },
  filters: {
    type: Object,
    default: () => ({ status: [], archived: "Do Not Show", types: [], search: "" }),
  },
});

const emit = defineEmits(["input", "apply", "reset"]);

const DEFAULT_STATUSES = { Draft: true, Active: true, Retired: false };
const DEFAULT_ARCHIVED = "Do Not Show";
const DEFAULT_TYPES = { Role: false, Responsibility: false };

const statusOptions = [
  { label: "Draft", value: "Draft" },
  { label: "Active", value: "Active" },
  { label: "Retired", value: "Retired" },
];
const typeOptions = [
  { label: "Role", value: "Role" },
  { label: "Responsibility", value: "Responsibility" },
];
const archivedOptions = [{ label: "Do Not Show" }, { label: "Show" }];
const archivedClearKey = ref("");

const statuses = ref({ ...DEFAULT_STATUSES });
const archived = ref(DEFAULT_ARCHIVED);
const types = ref({ ...DEFAULT_TYPES });

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

function syncFromProps() {
  const filters = props.filters || {};
  statuses.value = {
    Draft: (filters.status || []).includes("Draft"),
    Active: (filters.status || []).includes("Active"),
    Retired: (filters.status || []).includes("Retired"),
  };
  archived.value = filters.archived === "Show" ? "Show" : "Do Not Show";
  types.value = {
    Role: (filters.types || []).includes("Role"),
    Responsibility: (filters.types || []).includes("Responsibility"),
  };
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function updateType(key, checked) {
  types.value = { ...types.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  archived.value = payload?.value === "Show" ? "Show" : "Do Not Show";
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  archived.value = DEFAULT_ARCHIVED;
  types.value = { ...DEFAULT_TYPES };
  archivedClearKey.value = `reset-${Date.now()}`;
  emit("reset");
}

function handleApply() {
  const selectedStatus = statusOptions
    .map((option) => option.value)
    .filter((key) => statuses.value[key]);
  const selectedTypes = typeOptions.map((option) => option.value).filter((key) => types.value[key]);
  emit("apply", {
    status: selectedStatus,
    archived: archived.value,
    types: selectedTypes,
    search: props.filters?.search || "",
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
}
</script>

<style scoped>
.prr-filters-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 24, 40, 0.5);
}
.prr-filters-modal__card {
  width: 640px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 24px rgba(16, 24, 40, 0.12);
}
.prr-filters-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.prr-filters-modal__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}
.prr-filters-modal__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
}
.prr-filters-modal__filters-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.prr-filters-modal__column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.prr-filters-modal__column-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}
.prr-filters-modal__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.prr-filters-modal__divider {
  height: 1px;
  background: #eaecf0;
  margin: 20px 0;
}
.prr-filters-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.prr-filters-modal__reset {
  border: none;
  background: transparent;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
}
.prr-filters-modal__reset:hover {
  color: #344054;
}
</style>
